import PortalIdParser from 'PortalIdParser';
import { ChatMessageCitationTypename, LlmResponseSectionTypename, MemoryCitationTypename, WebPageCitationTypename } from 'chatspot-core/__generated__/chirp/com/hubspot/copilot/rpc/v2/CopilotMessageService';
const BZE_CITATION_TYPE_TO_TYPENAME = {
  MEMORY: MemoryCitationTypename,
  CHAT_MESSAGE: ChatMessageCitationTypename
};
function buildCitationPayload(citation) {
  var _ref, _BZE_CITATION_TYPE_TO, _citation$citationTyp, _inAppIds$conversatio;
  const typename = (_ref = (_BZE_CITATION_TYPE_TO = BZE_CITATION_TYPE_TO_TYPENAME[(_citation$citationTyp = citation.citationType) !== null && _citation$citationTyp !== void 0 ? _citation$citationTyp : '']) !== null && _BZE_CITATION_TYPE_TO !== void 0 ? _BZE_CITATION_TYPE_TO : citation.citationType) !== null && _ref !== void 0 ? _ref : WebPageCitationTypename;
  const inAppIds = citation.inAppIds;
  return Object.assign({}, citation, {
    __typename: typename
  }, typename === MemoryCitationTypename ? {
    memoryText: citation.title
  } : {}, typename === ChatMessageCitationTypename ? Object.assign({
    messageContent: citation.title,
    threadId: (_inAppIds$conversatio = inAppIds === null || inAppIds === void 0 ? void 0 : inAppIds.conversationId) !== null && _inAppIds$conversatio !== void 0 ? _inAppIds$conversatio : ''
  }, inAppIds !== null && inAppIds !== void 0 && inAppIds.messageId ? {
    messageId: inAppIds.messageId
  } : {}) : {});
}
export function buildTextBlockMessagePayload(textBlock, sectionId, chatMessage) {
  var _textBlock$entries, _chatMessage$turnId;
  const citations = [];
  const crmRecordChips = [];
  for (const citation of (_textBlock$citations = textBlock.citations) !== null && _textBlock$citations !== void 0 ? _textBlock$citations : []) {
    var _textBlock$citations;
    if (citation.displayType === 'RECORD_CHIP') {
      var _citation$secondaryPr;
      // citation.url is resolved by breeze-engine-lib from the EMBEDDED surface URL
      // on the citation's urls array. Convert it back to the CrmRecordChip urls format
      // so CrmRecordChip can navigate to the correct URL rather than falling back to
      // the generic /contacts/{portalId}/record/{objectTypeId}/{objectId} pattern.
      crmRecordChips.push({
        objectTypeId: citation.objectTypeId,
        objectId: citation.objectId,
        displayName: citation.title,
        urls: citation.url ? [{
          clientSurface: 'EMBEDDED',
          url: citation.url
        }] : [],
        primaryProperty: citation.primaryProperty,
        secondaryProperties: (_citation$secondaryPr = citation.secondaryProperties) !== null && _citation$secondaryPr !== void 0 ? _citation$secondaryPr : [],
        citationId: citation.id
      });
      continue;
    }
    if (citation.index === undefined) continue;
    citations[citation.index - 1] = buildCitationPayload(citation);
  }

  // RECORD_CHIP entries must render as inline CRM chips, not dynamic sections.
  // resolveCitations moves them from DISPLAY_ONLY blocks into textBlock.entries
  // and rewrites [cite-xxx] → [RESPONSE_SECTION_N] in the text. We undo that
  // by converting the marker back to [CRM_RECORD:...] and populating crmRecordChips.
  // RECORD_CHIP entries are excluded from dynamicSectionContainers since they are
  // fully handled via crmRecordChips and any code iterating containers directly
  // would encounter a typename the section registry cannot handle.
  let sectionText = textBlock.text;
  const dynamicSectionContainers = [];
  let dynamicSectionSlot = 0;
  ((_textBlock$entries = textBlock.entries) !== null && _textBlock$entries !== void 0 ? _textBlock$entries : []).forEach((entry, index) => {
    var _entry$citationId;
    const value = entry.value;
    if ((value === null || value === void 0 ? void 0 : value.displayType) === 'RECORD_CHIP') {
      var _value$objectTypeId, _value$objectId, _value$title, _urls$find$url, _urls$find, _ref2;
      const objectTypeId = String((_value$objectTypeId = value.objectTypeId) !== null && _value$objectTypeId !== void 0 ? _value$objectTypeId : '');
      const objectId = String((_value$objectId = value.objectId) !== null && _value$objectId !== void 0 ? _value$objectId : '');
      const title = String((_value$title = value.title) !== null && _value$title !== void 0 ? _value$title : '');
      const urls = Array.isArray(value.urls) ? value.urls : [];
      const embeddedUrl = (_urls$find$url = (_urls$find = urls.find(u => u.clientSurface === 'EMBEDDED')) === null || _urls$find === void 0 ? void 0 : _urls$find.url) !== null && _urls$find$url !== void 0 ? _urls$find$url : '';
      crmRecordChips.push({
        objectTypeId,
        objectId,
        displayName: title,
        urls: embeddedUrl ? [{
          clientSurface: 'EMBEDDED',
          url: embeddedUrl
        }] : [],
        primaryProperty: value.primaryProperty,
        secondaryProperties: (_ref2 = value.secondaryProperties) !== null && _ref2 !== void 0 ? _ref2 : [],
        citationId: entry.citationId
      });
      sectionText = sectionText.replace(`[RESPONSE_SECTION_${index + 1}]`, `[CRM_RECORD:${objectTypeId}:${objectId}:${encodeURIComponent(title)}]`);
      return;
    }
    const newSlot = ++dynamicSectionSlot;
    if (newSlot !== index + 1) {
      sectionText = sectionText.replace(`[RESPONSE_SECTION_${index + 1}]`, `[RESPONSE_SECTION_${newSlot}]`);
    }
    // Collect RECORD_CHIP citations whose [cite-xxx] markers were resolved in
    // this entry's text (e.g. a LlmResponseSection from crmVisualizeSqlResults,
    // which renders native HubSpot CRM reports; visualizeNonCrmResults handles
    // non-CRM/static visualizations like Highcharts and doesn't emit CRM
    // record citations).
    for (const citation of (_entry$resolvedCitati = entry.resolvedCitations) !== null && _entry$resolvedCitati !== void 0 ? _entry$resolvedCitati : []) {
      var _entry$resolvedCitati;
      if (citation.displayType === 'RECORD_CHIP') {
        var _citation$secondaryPr2;
        crmRecordChips.push({
          objectTypeId: citation.objectTypeId,
          objectId: citation.objectId,
          displayName: citation.title,
          urls: citation.url ? [{
            clientSurface: 'EMBEDDED',
            url: citation.url
          }] : [],
          primaryProperty: citation.primaryProperty,
          secondaryProperties: (_citation$secondaryPr2 = citation.secondaryProperties) !== null && _citation$secondaryPr2 !== void 0 ? _citation$secondaryPr2 : []
        });
      }
    }
    dynamicSectionContainers.push({
      // Use citationId as the container ID so [cite-<id>] markers in text can
      // resolve to this section. Fall back to entry.id for non-citation entries.
      id: (_entry$citationId = entry.citationId) !== null && _entry$citationId !== void 0 ? _entry$citationId : entry.id,
      contextSummary: '',
      // BZE tool payloads (e.g. IMAGE_GRID from generateImage) arrive as plain
      // JSON without a FQ __typename — only a short `type` field. Merge
      // entry.__typename (extracted by toDisplayOnlyEntry) so the section
      // registry lookup in CitationProcessedMarkdown has something to key on.
      responseSection: Object.assign({
        __typename: entry.__typename
      }, entry.value, entry.resolvedText ? {
        text: entry.resolvedText
      } : {})
    });
  });
  return {
    threadId: (_chatMessage$turnId = chatMessage === null || chatMessage === void 0 ? void 0 : chatMessage.turnId) !== null && _chatMessage$turnId !== void 0 ? _chatMessage$turnId : 0,
    portalId: PortalIdParser.get(),
    finalResponse: !!(chatMessage !== null && chatMessage !== void 0 && chatMessage.stopReason),
    sections: [{
      text: sectionText,
      id: sectionId,
      __typename: LlmResponseSectionTypename
    }],
    dynamicSectionContainers,
    citations,
    id: sectionId,
    timestamp: 0,
    messageType: 'COPILOT',
    interactionId: '',
    modelRequestIds: [],
    voiceFiller: false,
    agentPayload: {},
    compoundCitationKeys: {},
    compoundSectionKeys: {},
    crmRecordChips
  };
}

// BreezeEngine ChatMessage doesn't map 1:1 to the BA Message type.
// We provide the fields section renderers commonly read; everything else defaults.
export function buildDisplayOnlyMessagePayload(chatMessage, resolvedCitations = []) {
  var _chatMessage$turnId2, _chatMessage$id;
  const citations = [];
  const crmRecordChips = [];
  for (const citation of resolvedCitations) {
    if (citation.displayType === 'RECORD_CHIP') {
      var _citation$secondaryPr3;
      crmRecordChips.push({
        objectTypeId: citation.objectTypeId,
        objectId: citation.objectId,
        displayName: citation.title,
        urls: citation.url ? [{
          clientSurface: 'EMBEDDED',
          url: citation.url
        }] : [],
        primaryProperty: citation.primaryProperty,
        secondaryProperties: (_citation$secondaryPr3 = citation.secondaryProperties) !== null && _citation$secondaryPr3 !== void 0 ? _citation$secondaryPr3 : []
      });
      continue;
    }
    if (citation.index === undefined) continue;
    citations[citation.index - 1] = buildCitationPayload(citation);
  }
  return {
    threadId: (_chatMessage$turnId2 = chatMessage === null || chatMessage === void 0 ? void 0 : chatMessage.turnId) !== null && _chatMessage$turnId2 !== void 0 ? _chatMessage$turnId2 : 0,
    portalId: PortalIdParser.get(),
    finalResponse: !!(chatMessage !== null && chatMessage !== void 0 && chatMessage.stopReason),
    sections: [],
    dynamicSectionContainers: [],
    citations,
    id: (_chatMessage$id = chatMessage === null || chatMessage === void 0 ? void 0 : chatMessage.id) !== null && _chatMessage$id !== void 0 ? _chatMessage$id : '',
    timestamp: 0,
    messageType: 'COPILOT',
    interactionId: '',
    modelRequestIds: [],
    voiceFiller: false,
    agentPayload: {},
    compoundCitationKeys: {},
    compoundSectionKeys: {},
    crmRecordChips
  };
}
export function buildDisplayOnlySectionsPayload(block) {
  var _block$entries;
  return ((_block$entries = block.entries) !== null && _block$entries !== void 0 ? _block$entries : []).filter(entry => {
    var _entry$value;
    return (
      // Citation entries (citationId set) are for inline CRM record chips
      // resolved at citation-tooltip time -- they have no section formatter
      // and must not reach AISectionsRenderer.
      !entry.citationId && ((_entry$value = entry.value) === null || _entry$value === void 0 ? void 0 : _entry$value.displayType) !== 'RECORD_CHIP'
    );
  }).map(entry => {
    if (!entry.resolvedText) return entry.value;
    return Object.assign({}, entry.value, {
      text: entry.resolvedText
    });
  });
}